'use client';

import { Button } from '@/components/ui/button';

const plans = [
    {
        name: 'Free',
        price: '$0',
        description: 'For learning and basic LaTeX editing',
        badge: null,
        cta: 'Get Started',
        highlighted: false,
        priceNote: '',
        features: [
            { name: 'Local LaTeX editing', included: true },
            { name: 'Manual compile', included: true },
            { name: 'Single project', included: true },
            { name: 'No cloud sync', included: true },
        ],
    },
    {
        name: 'Pro',
        price: '$19.99',
        priceNote: '/month',
        description: 'Professional LaTeX editing with offline support',
        badge: 'Recommended',
        cta: 'Get Started',
        highlighted: true,
        features: [
            { name: 'Unlimited projects', included: true },
            { name: 'Offline-first workflow', included: true },
            { name: 'Cloud sync', included: true },
            { name: 'Faster compile', included: true },
            { name: 'Collaborate on projects', included: true },
        ],
    },
    {
        name: 'Student',
        price: '$0',
        description: 'Essential LaTeX tools for academic success',
        badge: null,
        cta: 'Get Started',
        highlighted: false,
        priceNote: '',
        features: [
            { name: 'Unlimited projects', included: true },
            { name: 'Offline-first workflow', included: true },
            { name: 'Cloud sync', included: true },
            { name: 'Faster compile', included: true },
            { name: 'Collaborate on projects', included: true },
        ],
    },
];

const comparisonFeatures = [
    { feature: 'Projects', free: '1', pro: 'Unlimited', student: 'Unlimited' },
    { feature: 'Offline support', free: '✓', pro: '✓', student: '✓' },
    { feature: 'Cloud sync', free: '—', pro: '✓', student: '✓' },
    { feature: 'Collaboration', free: '—', pro: '✓', student: '✓' },
    { feature: 'Support', free: 'Community', pro: 'Email', student: 'Email' },
];

export default function LandingPricingPage() {
    return (
        <>
            <main>
                <section className="bg-background py-16 md:py-24 px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">
                                Choose the plan that fits{' '}
                                <span className="text-primary">your workflow</span>
                            </h1>
                            <p className="text-lg text-muted-foreground">
                                Professional LaTeX editing with offline support and cloud sync.
                            </p>
                        </div>

                        {/* Pricing Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                            {plans.map((plan) => (
                                <div
                                    key={plan.name}
                                    className={`
        relative rounded-xl border px-6 py-6
        ${plan.highlighted
                                            ? "border-blue-500 bg-blue-50 dark:bg-blue-500/10"
                                            : "border-border bg-card"}
      `}
                                >

                                    {/* Badge (TOP RIGHT — FIXED) */}
                                    {plan.badge && (
                                        <div className="absolute top-3 right-3">
                                            <span className="bg-blue-500 text-white text-[11px] px-2.5 py-1 rounded-md">
                                                {plan.badge}
                                            </span>
                                        </div>
                                    )}

                                    {/* Title */}
                                    <h3 className="text-[18px] font-semibold text-foreground">
                                        {plan.name}
                                    </h3>

                                    <p className="text-[13px] text-muted-foreground mt-1">
                                        {plan.description}
                                    </p>

                                    {/* Price */}
                                    <div className="mt-5 flex items-end gap-2">
                                        <span className="text-[28px] font-bold text-foreground">
                                            {plan.price}
                                        </span>
                                        {plan.priceNote && (
                                            <span className="text-[13px] text-muted-foreground mb-[3px]">
                                                {plan.priceNote}
                                            </span>
                                        )}
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-border my-5" />

                                    {/* Features */}
                                    <div className="space-y-3 mb-6">
                                        {plan.features.map((feature, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-[14px] text-foreground/80">
                                                <div className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                                                    <span className="text-[10px] text-blue-600 dark:text-blue-400">✓</span>
                                                </div>
                                                {feature.name}
                                            </div>
                                        ))}
                                    </div>

                                    {/* CTA (3 VARIANTS — EXACT FIGMA) */}
                                    <Button
                                        className={`
          w-full h-[42px] text-sm rounded-md
          ${plan.highlighted
                                                ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600"
                                                : plan.name === "Student"
                                                    ? "bg-[#5B8CFF24] border border-[#5B8CFF] text-[#2F6BFF] hover:bg-[#5B8CFF33]"
                                                    : "border border-border bg-card text-foreground/80 hover:bg-muted"
                                            }
        `}
                                    >
                                        {plan.cta}
                                    </Button>

                                </div>
                            ))}
                        </div>

                        {/* CTA Section */}
                        <div className="bg-card border border-[#5B8CFF]/70 rounded-[12px] py-10 px-6 text-center mb-14">

                            {/* TEXT */}
                            <p className="text-[18px] leading-[38px] tracking-[0.34px] text-muted-foreground mb-6">
                                Experience the power of Rudrise in your browser.
                            </p>

                            {/* BUTTON */}
                            <Button
                                className="
      h-[36px]
      px-[14px]
      rounded-[12px]
      bg-[#5B8CFF]
      border border-[#5B8CFF]
      text-white text-sm font-medium
      hover:bg-[#4c7cf7]
      transition-all duration-150 ease-out
      shadow-none
    "
                            >
                                Open Web Editor (Free)
                            </Button>

                        </div>

                        {/* Comparison Table */}
                        <div>
                            <h2 className="text-2xl font-semibold mb-6 text-foreground">
                                Feature Comparison
                            </h2>

                            <div className="overflow-hidden rounded-xl border border-border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="text-left py-3 px-4 font-medium text-foreground/80">Feature</th>
                                            <th className="text-center py-3 px-4 font-medium text-foreground">Free</th>
                                            <th className="text-center py-3 px-4 font-medium text-foreground">Pro</th>
                                            <th className="text-center py-3 px-4 font-medium text-foreground">Student</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {comparisonFeatures.map((row, idx) => (
                                            <tr key={idx} className={idx % 2 === 0 ? "bg-muted" : "bg-card"}>
                                                <td className="py-3 px-4 text-foreground/90">{row.feature}</td>

                                                <td className="text-center py-3 px-4 text-muted-foreground">
                                                    {row.free}
                                                </td>

                                                <td className="text-center py-3 px-4 text-blue-600 dark:text-blue-400 font-medium">
                                                    {row.pro}
                                                </td>

                                                <td className="text-center py-3 px-4 text-blue-600 dark:text-blue-400 font-medium">
                                                    {row.student}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
